import { Component, Input } from "@angular/core";
import { NgbActiveModal, NgbModal, NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { Observable, of } from "rxjs";
import { filter, map, shareReplay, switchMap, take } from "rxjs/operators";
import { Listing } from "../shared/interfaces/listing";
import { ListingHistory } from "./listing-history";
import { ListingHistoryService } from "./listing-history.service";
import { selectListingAudit, selectListingHistories } from "./history.selectors";
import {
	AlertService,
	distinctUntilJsonChanged,
	SessionService,
} from "@recore-solutions/nstar-lib";
import { Store } from "@ngrx/store";
import { selectListingServiceOffering } from "../listing-service-offering.selectors";
import { ListingAuditService } from "./listing-audit.service";
import { ListingAudit } from "./listing-audit";
import { ConfirmDeletionModalComponent } from "./confirm-deletion-modal/confirm-deletion-modal.component";
import { ListingAuditDataService } from "./listing-audit-data.service";
import { PreferencesService } from "../preferences/preferences.service";
import { groupData } from "../shared/group-by-dttm";

@Component({
	selector: "app-history",
	templateUrl: "./history.component.html",
	styleUrls: ["./history.component.scss"],
})
export class HistoryComponent {
	@Input() public listingOrganizationSK: number;
	activeTab: string = "Modification Log";
	listingHistory$: Observable<ListingHistory[]>;
	listingHistoryGroup$: Observable<ListingHistory[][]>;

	listingAudit$: Observable<ListingAudit[]>;
	listingMetadata$: Observable<Listing>;
	hasListingAuditPermissions$: Observable<boolean>;
	active = 1;
	selectedItems: any[] = [];

	listingHistoryLoading$: Observable<boolean>;
	listingAuditLoading$: Observable<boolean>;
	defaultDifferencesToggle$: Observable<boolean>;

	constructor(
		private listingHistoryService: ListingHistoryService,
		private listingAuditService: ListingAuditService,
		private listingAuditDataService: ListingAuditDataService,
		private sessionService: SessionService,
		private preferencesService: PreferencesService,
		private store: Store,
		private activeModal: NgbActiveModal,
		private alertService: AlertService,
		private ngbModal: NgbModal
	) {
		this.listingHistory$ = this.listingHistoryService.store
			.select(selectListingHistories)
			.pipe(shareReplay({ refCount: true, bufferSize: 1 }));
		this.listingAudit$ = this.listingAuditService.store
			.select(selectListingAudit)
			.pipe(shareReplay({ refCount: true, bufferSize: 1 }));
		this.listingHistoryGroup$ = this.listingHistory$.pipe(
			filter((listingHistories: ListingHistory[]) => listingHistories.length > 0),
			distinctUntilJsonChanged(),
			map((data: ListingHistory[]) => groupData(data, "modificationTimestampUTCDTTM"))
		);

		this.defaultDifferencesToggle$ = this.preferencesService
			.getPreference(["listing", "historyDiff"])
			.pipe(
				map((value) => {
					let showDifferences = false;
					if (value && value === "show") {
						showDifferences = true;
					}
					return showDifferences;
				}),
				shareReplay({
					refCount: true,
					bufferSize: 1,
				})
			);

		this.hasListingAuditPermissions$ = this.store
			.select(selectListingServiceOffering("ListingAudit"))
			.pipe(
				switchMap((offering) => {
					let returnValue: Observable<boolean>;

					switch (offering.accessName.toLowerCase()) {
						case "administrator":
							returnValue = of(true);
							break;
						case "organization":
							returnValue = this.sessionService.memberBasic$.pipe(
								map((memberBasics) => memberBasics.organizationSK === this.listingOrganizationSK)
							);
							break;
						default:
							returnValue = of(false);
					}
					return returnValue;
				})
			);

		this.listingHistoryLoading$ = this.listingHistoryService.loading$.pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);
		this.listingAuditLoading$ = this.listingAuditService.loading$.pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	onNavChange(event: NgbNavChangeEvent) {
		switch (event.nextId) {
			case 1:
				this.activeTab = "Modification Log";
				break;

			case 2:
				this.activeTab = "Listing History";
				break;

			default:
				this.activeTab = "";
				break;
		}
	}

	close = (): void => {
		this.activeModal.close();
	};

	deleteListingAudit($event: ListingAudit) {
		const modalRef = this.ngbModal.open(ConfirmDeletionModalComponent, {
			centered: true,
		});
		modalRef.componentInstance.listingAudit = $event;

		modalRef.result.then((data?: ListingAudit) => {
			if (data) {
				this.listingAuditDataService
					.deleteAuditRecord({
						listingSK: data.listingSK,
						listingAuditSK: data.listingAuditSK,
					})
					.pipe(take(1))
					.subscribe({
						next: (result) => {
							this.listingAuditService.clearCache();
							this.listingAuditService.addManyToCache(result);
							this.alertService.alertSuccess("Successfully deleted Listing History record");
						},
						error: () => this.alertService.alertDanger("Failed to delete Listing History record"),
					});
			}
		});
	}
}
